import { useState, useEffect } from 'react'
import { loadState, saveState } from '../utils/storage'

/**
 * Custom hook for managing localStorage state
 * @returns {[Object, Function]} Current state and setState function
 */
export const useLocalStorage = () => {
  const [state, setStateInternal] = useState(() => loadState())

  const setState = (newState) => {
    setStateInternal(newState)
    saveState(newState)
  }

  // Update a specific path in state
  const updateState = (path, value) => {
    setState((prevState) => {
      const newState = { ...prevState }
      const keys = path.split('.')
      let current = newState

      for (let i = 0; i < keys.length - 1; i++) {
        if (!current[keys[i]]) {
          current[keys[i]] = {}
        }
        current = current[keys[i]]
      }

      current[keys[keys.length - 1]] = value
      return newState
    })
  }

  // Get a specific path from state
  const getState = (path) => {
    const keys = path.split('.')
    let current = state

    for (const key of keys) {
      if (current === undefined || current === null) {
        return undefined
      }
      current = current[key]
    }

    return current
  }

  return [state, setState, updateState, getState]
}

/**
 * Hook for managing user settings
 * @param {Object} state - Current app state
 * @param {Function} updateState - State update function
 * @returns {Object} Settings and update functions
 */
export const useSettings = (state, updateState) => {
  const settings = state.user?.settings || {}

  const updateSetting = (key, value) => {
    updateState(`user.settings.${key}`, value)
  }

  const toggleTheme = () => {
    const newTheme = settings.theme === 'light' ? 'dark' : 'light'
    updateSetting('theme', newTheme)
    
    // Update document class for Tailwind dark mode
    if (newTheme === 'dark') {
      document.documentElement.classList.add('dark')
    } else {
      document.documentElement.classList.remove('dark')
    }
  }

  const setFontSize = (size) => {
    updateSetting('fontSize', size)
    
    // Update document font size
    const sizeMap = {
      small: '14px',
      medium: '16px',
      large: '18px',
    }
    document.documentElement.style.fontSize = sizeMap[size] || '16px'
  }

  // Initialize theme and font size on mount
  useEffect(() => {
    if (settings.theme === 'dark') {
      document.documentElement.classList.add('dark')
    }
    
    const sizeMap = {
      small: '14px',
      medium: '16px',
      large: '18px',
    }
    document.documentElement.style.fontSize = sizeMap[settings.fontSize] || '16px'
  }, [])

  return {
    settings,
    updateSetting,
    toggleTheme,
    setFontSize,
  }
}
